import {
  pgTable,
  text,
  uuid,
  char,
  boolean,
  timestamp,
  integer,
  smallint,
  jsonb,
  pgEnum,
  primaryKey,
  numeric,
  date,
  unique,
} from "drizzle-orm/pg-core";
import { relations, sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const empresaTipoEnum = pgEnum("empresa_tipo", [
  "OWNER",
  "VISTORIADORA",
  "PARCEIRO_DE_NEGOCIOS",
]);
export const laudoStatusEnum = pgEnum("laudo_status", [
  "rascunho",
  "enviado_analise",
  "em_vistoria",
  "em_avaliacao_rt",
  "assinado",
  "finalizado",
]);
export const tipoVeiculoEnum = pgEnum("tipo_veiculo", [
  "auto_leve",
  "moto",
  "caminhao",
  "onibus",
]);
export const tipoDestinacaoEnum = pgEnum("tipo_destinacao", [
  "venda_imediata",
  "recuperacao",
  "inexistente",
  "descarte",
]);
export const faturaStatusEnum = pgEnum("fatura_status", [
  "pendente",
  "pago",
  "vencido",
  "cancelado",
  "atrasada",
  "bloqueada",
]);

export const empresas = pgTable("empresas", {
  id: uuid("id").primaryKey().defaultRandom(),
  tipo: empresaTipoEnum("tipo").notNull(),
  razaoSocial: text("razao_social").notNull(),
  nomeFantasia: text("nome_fantasia"),
  cnpj: text("cnpj"),
  cpf: text("cpf"),
  uf: char("uf", { length: 2 }).notNull(),
  email: text("email").notNull(),
  telefone: text("telefone"),
  whatsapp: text("whatsapp"),
  logradouro: text("logradouro"),
  numero: text("numero"),
  complemento: text("complemento"),
  bairro: text("bairro"),
  cidade: text("cidade").notNull(),
  cep: char("cep", { length: 8 }),
  asaasCompanyType: text("asaas_company_type"),
  asaasTaxRegime: text("asaas_tax_regime"),
  asaasIncomeValue: numeric("asaas_income_value", { precision: 12, scale: 2 }),
  vistoriadoraId: uuid("vistoriadora_id").references((): any => empresas.id, {
    onDelete: "set null",
  }),
  codigoConvite: text("codigo_convite").unique(),
  tagEmpresa: text("tag_empresa").unique(),
  tagParceiro: text("tag_parceiro"),
  logoUrl: text("logo_url"),
  valorPorLaudo: numeric("valor_por_laudo", { precision: 10, scale: 2 }),
  valoresPorTipo: jsonb("valores_por_tipo"),
  idProprio: integer("id_proprio"),
  // ART da empresa parceira de negócios
  art: text("art"),
  vencimentoArt: date("vencimento_art"),
  ativo: boolean("ativo").notNull().default(true),
  // Modo de teste: dispensa certificado digital e habilita botões de mock (por empresa)
  modoTeste: boolean("modo_teste").notNull().default(false),
  // Só relevante na linha OWNER (singleton): novas empresas nascem em modo teste?
  modoTesteNovosCadastros: boolean("modo_teste_novos_cadastros")
    .notNull()
    .default(false),
  // Só relevante em linhas VISTORIADORA com modoTeste=true e módulo Crédito Pré-Pago
  // ativo: como a compra de crédito dos Parceiros dela se comporta em modo teste.
  // 'sandbox_asaas' (default) = fluxo real de hoje, fala com o Sandbox do Asaas.
  // 'simulacao' = tela de checkout falsa, QA escolhe o resultado na hora.
  modoTesteCreditoMetodo: text("modo_teste_credito_metodo")
    .notNull()
    .default("sandbox_asaas"),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const userProfiles = pgTable("user_profiles", {
  id: uuid("id").primaryKey(),
  empresaId: uuid("empresa_id")
    .references(() => empresas.id)
    .notNull(),
  nome: text("nome").notNull(),
  email: text("email").notNull(),
  roles: text("roles").array().notNull().default([]),
  cpf: text("cpf"),
  telefone: text("telefone"),
  whatsapp: text("whatsapp"),
  logradouro: text("logradouro"),
  numero: text("numero"),
  complemento: text("complemento"),
  bairro: text("bairro"),
  cidade: text("cidade"),
  estado: char("estado", { length: 2 }),
  cep: char("cep", { length: 8 }),
  crea: text("crea"),
  assinaturaUrl: text("assinatura_url"),
  ativo: boolean("ativo").notNull().default(true),
  rebaixadoEm: timestamp("rebaixado_em", { withTimezone: true }),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const inviteCodes = pgTable("invite_codes", {
  id: uuid("id").primaryKey().defaultRandom(),
  code: text("code").notNull().unique(),
  tipo: empresaTipoEnum("tipo").notNull(),
  vistoriadoraId: uuid("vistoriadora_id").references(() => empresas.id, {
    onDelete: "cascade",
  }),
  usos: integer("usos").notNull().default(0),
  maxUsos: integer("max_usos"),
  ativo: boolean("ativo").notNull().default(true),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const userInvites = pgTable("user_invites", {
  id: uuid("id").primaryKey().defaultRandom(),
  code: text("code").notNull().unique(),
  email: text("email"),
  empresaId: uuid("empresa_id")
    .notNull()
    .references(() => empresas.id, { onDelete: "cascade" }),
  roles: text("roles").array().notNull().default(["tv"]),
  usos: integer("usos").notNull().default(0),
  maxUsos: integer("max_usos").default(1),
  ativo: boolean("ativo").notNull().default(true),
  criadoPor: uuid("criado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const laudos = pgTable("laudos", {
  id: uuid("id").primaryKey(),
  laudoId: text("laudo_id").unique(),
  vistoriadoraId: uuid("vistoriadora_id")
    .references(() => empresas.id)
    .notNull(),
  parceiroId: uuid("cliente_id")
    .references(() => empresas.id)
    .notNull(),
  cartela: char("cartela", { length: 10 }),
  cartelaConfirmada: boolean("cartela_confirmada").notNull().default(false),
  status: laudoStatusEnum("status").notNull().default("rascunho"),
  tecnicoVistoriaId: uuid("tecnico_vistoria_id").references(
    () => userProfiles.id,
  ),
  responsavelTecnicoId: uuid("responsavel_tecnico_id").references(
    () => userProfiles.id,
  ),
  assinaturaUrl: text("assinatura_url"),
  pdfUrl: text("pdf_url"),
  baixa: boolean("baixa").notNull().default(false),
  baixaEm: timestamp("baixa_em", { withTimezone: true }),
  baixaPor: uuid("baixa_por").references(() => userProfiles.id),
  baixaChaveNota: text("baixa_chave_nota"),
  baixaNotaPath: text("baixa_nota_path"),
  baixaNotaNome: text("baixa_nota_nome"),
  baixaNotaMime: text("baixa_nota_mime"),
  dataVistoria: timestamp("data_vistoria", { withTimezone: true }),
  dataAssinatura: timestamp("data_assinatura", { withTimezone: true }),
  veiculoId: uuid("veiculo_id").references((): any => veiculos.id, {
    onDelete: "set null",
  }),
  criadoPor: uuid("criado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  // Preenchida só pelo servidor (trigger/funções de crédito); nunca vem do cliente.
  aguardandoCreditoDesde: timestamp("aguardando_credito_desde", { withTimezone: true }),
  // Estado do módulo Detran na criação (fixado por trigger; não muda depois)
  moduloDetranVeiculoFoto: boolean("modulo_detran_veiculo_foto").notNull().default(false),
  // Pedido do Parceiro de envio direto ao Técnico de Vistoria (TV único). Gravado uma vez pela rota
  // dedicada; quem atribui o técnico é o trigger do banco (migrations/modulo_detran_veiculo_foto.sql, seção 7).
  envioTvSolicitadoEm: timestamp("envio_tv_solicitado_em", { withTimezone: true }),
});

export const pecas = pgTable("pecas", {
  id: uuid("id").primaryKey().defaultRandom(),
  nome: text("nome").notNull(),
  tipoVeiculo: tipoVeiculoEnum("tipo_veiculo").notNull(),
  ufs: text("ufs").array().notNull().default([]),
  ufSequencias: jsonb("uf_sequencias").notNull().default({}),
  ativo: boolean("ativo").notNull().default(true),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const lotes = pgTable("lotes", {
  id: uuid("id").primaryKey().defaultRandom(),
  vistoriadoraId: uuid("vistoriadora_id")
    .references(() => empresas.id)
    .notNull(),
  // null = lote geral; preenchido = lote por parceiro (quando modeLote = 'por_parceiro')
  parceiroId: uuid("parceiro_id").references(() => empresas.id, {
    onDelete: "set null",
  }),
  mes: smallint("mes").notNull(),
  ano: smallint("ano").notNull(),
  qtdLaudos: integer("qtd_laudos").notNull().default(0),
  fechado: boolean("fechado").notNull().default(false),
  fechadoEm: timestamp("fechado_em", { withTimezone: true }),
  fechamentoPrevisto: timestamp("fechamento_previsto", { withTimezone: true }),
  ordemServico: text("ordem_servico"),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const empresaSeq = pgTable("empresa_seq", {
  empresaId: uuid("empresa_id")
    .references(() => empresas.id, { onDelete: "cascade" })
    .notNull(),
  ultimoNum: integer("ultimo_num").notNull().default(0),
});

// Perguntas padrão por tipo de veículo (fallback quando a peça não tem perguntas específicas)
export const perguntasPadraoTipo = pgTable("perguntas_padrao_tipo", {
  id: uuid("id").primaryKey().defaultRandom(),
  tipoVeiculo: tipoVeiculoEnum("tipo_veiculo").notNull(),
  texto: text("texto").notNull(),
  sequencia: smallint("sequencia").notNull(),
  ativo: boolean("ativo").notNull().default(true),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// Banco global de perguntas reutilizáveis
export const bancoPerguntasGlobais = pgTable("banco_perguntas_globais", {
  id: uuid("id").primaryKey().defaultRandom(),
  texto: text("texto").notNull(),
  ativo: boolean("ativo").notNull().default(true),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// Catálogo de perguntas (UF-agnóstico)
export const perguntas = pgTable("perguntas", {
  id: uuid("id").primaryKey().defaultRandom(),
  texto: text("texto").notNull(),
  ativo: boolean("ativo").notNull().default(true),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  sequencia: smallint("sequencia").notNull(),
});

export const faturas = pgTable("faturas", {
  id: uuid("id").primaryKey().defaultRandom(),
  vistoriadoraId: uuid("vistoriadora_id").references(() => empresas.id),
  loteId: uuid("lote_id").references(() => lotes.id, { onDelete: "set null" }),
  numero: integer("numero").notNull(),
  mes: smallint("mes").notNull(),
  ano: smallint("ano").notNull(),
  status: faturaStatusEnum("status").notNull().default("pendente"),
  valoresPorTipo: jsonb("valores_por_tipo"),
  valorTotal: numeric("valor_total", { precision: 10, scale: 2 }).notNull(),
  multa: numeric("multa", { precision: 10, scale: 2 }).default("0"),
  juros: numeric("juros", { precision: 10, scale: 2 }).default("0"),
  vencimento: date("vencimento").notNull(),
  diasAtraso: integer("dias_atraso").default(0),
  asaasPaymentId: text("asaas_payment_id"),
  asaasInvoiceUrl: text("asaas_invoice_url"),
  metodoPagamento: text("metodo_pagamento"),
  pagoEm: timestamp("pago_em", { withTimezone: true }),
  criadoPor: uuid("criado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const precosPlataforma = pgTable("precos_plataforma", {
  id: uuid("id").primaryKey().defaultRandom(),
  tipoVeiculo: tipoVeiculoEnum("tipo_veiculo").notNull().unique(),
  valor: numeric("valor", { precision: 10, scale: 2 }).notNull(),
  ativo: boolean("ativo").notNull().default(true),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  updatedBy: uuid("updated_by").references(() => userProfiles.id),
});

export const faturaItens = pgTable("fatura_itens", {
  id: uuid("id").primaryKey().defaultRandom(),
  faturaId: uuid("fatura_id")
    .notNull()
    .references(() => faturas.id, { onDelete: "cascade" }),
  laudoId: uuid("laudo_id").notNull(),
  tipoVeiculo: tipoVeiculoEnum("tipo_veiculo").notNull(),
  valorUnitario: numeric("valor_unitario", {
    precision: 10,
    scale: 2,
  }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const configFinanceiroEmpresa = pgTable("config_financeiro_empresa", {
  empresaId: uuid("empresa_id")
    .primaryKey()
    .references(() => empresas.id),
  multaPercentual: numeric("multa_percentual", { precision: 5, scale: 2 })
    .notNull()
    .default("2"),
  jurosDiario: numeric("juros_diario", { precision: 6, scale: 4 })
    .notNull()
    .default("0.0330"),
  ufFeriados: char("uf_feriados", { length: 2 }).notNull().default("SP"),
  // Imutável depois de preenchido; só ativar_asaas_cutover() grava (trigger no banco).
  asaasCutoverAt: timestamp("asaas_cutover_at", { withTimezone: true }),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  updatedBy: uuid("updated_by").references(() => userProfiles.id),
});

export const veiculos = pgTable("veiculos", {
  id: uuid("id").primaryKey().defaultRandom(),
  parceiroId: uuid("parceiro_id")
    .references(() => empresas.id, { onDelete: "cascade" })
    .notNull(),
  tipo: tipoVeiculoEnum("tipo").notNull(),
  placa: text("placa").notNull(),
  numeroNota: text("numero_nota").notNull(),
  chassi: text("chassi").notNull(),
  cor: text("cor").notNull(),
  marca: text("marca").notNull(),
  modelo: text("modelo").notNull(),
  anoFabricacao: integer("ano_fabricacao"),
  anoModelo: integer("ano_modelo"),
  numeroMotor: text("numero_motor"),
  renavam: text("renavam"),
  ativo: boolean("ativo").notNull().default(true),
  criadoPor: uuid("criado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const veiculoFotos = pgTable("veiculo_fotos", {
  id: uuid("id").primaryKey().defaultRandom(),
  veiculoId: uuid("veiculo_id").references(() => veiculos.id, { onDelete: "cascade" }).notNull(),
  parceiroId: uuid("parceiro_id").references(() => empresas.id, { onDelete: "cascade" }),
  posicao: text("posicao").notNull(),
  fotoUrl: text("foto_url").notNull(),
  enviadoPor: uuid("enviado_por").references(() => userProfiles.id, { onDelete: "set null" }),
  criadoEm: timestamp("criado_em", { withTimezone: true }).notNull().defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }).notNull().defaultNow(),
}, (t) => [unique("veiculo_fotos_veiculo_posicao_unica").on(t.veiculoId, t.posicao)]);
export type VeiculoFoto = typeof veiculoFotos.$inferSelect;

export const lancamentos = pgTable("lancamentos", {
  id: uuid("id").primaryKey().defaultRandom(),
  tipo: text("tipo").notNull(), // 'receita' | 'despesa'
  descricao: text("descricao").notNull(),
  valor: numeric("valor", { precision: 10, scale: 2 }).notNull(),
  categoria: text("categoria").notNull().default("outros"),
  data: date("data").notNull(),
  vistoriadoraId: uuid("vistoriadora_id").references(() => empresas.id, {
    onDelete: "set null",
  }),
  criadoPor: uuid("criado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// Peças de um laudo (normalizado – substitui o antigo array JSONB em laudos)
export const laudoPecas = pgTable("laudo_pecas", {
  id: uuid("id").primaryKey().defaultRandom(),
  laudoId: uuid("laudo_id")
    .references(() => laudos.id, { onDelete: "cascade" })
    .notNull(),
  catalogoPecaId: uuid("catalogo_peca_id").references(() => pecas.id, {
    onDelete: "set null",
  }),
  sequencia: integer("sequencia").notNull(),
  etiqueta: text("etiqueta").notNull(),
  uf: text("uf").notNull(),
  nome: text("nome").notNull(),
  tipoVeiculo: text("tipo_veiculo").notNull(),
  fotoUrl: text("foto_url"),
  observacaoParceiro: text("observacao_parceiro"),
  destinacao: tipoDestinacaoEnum("destinacao"),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow(),
});

// Respostas de cada peça de um laudo (substitui o antigo JSONB respostas em laudo_pecas)
export const laudoPecaRespostas = pgTable("laudo_peca_respostas", {
  id: uuid("id").primaryKey().defaultRandom(),
  laudoPecaId: uuid("laudo_peca_id")
    .references(() => laudoPecas.id, { onDelete: "cascade" })
    .notNull(),
  perguntaId: uuid("pergunta_id").references(() => perguntas.id, {
    onDelete: "set null", // histórico sobrevive mesmo se a pergunta for removida do catálogo
  }),
  resposta: boolean("resposta").notNull().default(false),
  outros: text("outros"),
  observacoes: text("observacoes"),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }).defaultNow(),
});

// Junção: associa cada peça (por UUID, já com UF) às suas perguntas
export const pecaPerguntas = pgTable(
  "peca_perguntas",
  {
    pecaId: uuid("peca_id")
      .references(() => pecas.id, { onDelete: "cascade" })
      .notNull(),
    perguntaId: uuid("pergunta_id")
      .references(() => perguntas.id, { onDelete: "cascade" })
      .notNull(),
  },
  (table) => [primaryKey({ columns: [table.pecaId, table.perguntaId] })],
);

// Configurações por vistoriadora: define comportamento de fechamento de lotes e ART
export const configuracoesVistoriadora = pgTable("configuracoes_vistoriadora", {
  id: uuid("id").primaryKey().defaultRandom(),
  vistoriadoraId: uuid("vistoriadora_id")
    .references(() => empresas.id, { onDelete: "cascade" })
    .notNull()
    .unique(),
  fechamentoAutomatico: boolean("fechamento_automatico")
    .notNull()
    .default(true),
  // false = ART Única (cada parceiro arca com sua ART)
  // true  = ART Múltipla (vistoriadora arca com a ART, armazenada em empresas.art)
  artMultipla: boolean("art_multipla").notNull().default(false),
  // Modo ART Única: false = ART por Parceiro de Negócio; true = ART por Responsável Técnico
  artPorRt: boolean("art_por_rt").notNull().default(false),
  // % do valor do crédito destinado à Vistoriadora (0–100). Configurado pelo Owner.
  percentualVistoriadora: numeric("percentual_vistoriadora", {
    precision: 5,
    scale: 2,
  })
    .notNull()
    .default("50.0"),
  // Laudos gratuitos por empresa-cliente (0 = nenhum)
  laudosGratisPorCliente: integer("laudos_gratis_por_cliente")
    .notNull()
    .default(0),
  // Quando true, fechamento de lote agrupa por cliente
  fecharLotePorCliente: boolean("fechar_lote_por_cliente")
    .notNull()
    .default(false),
  // Módulo: habilita sistema de créditos pré-pagos para parceiros desta vistoriadora
  creditosPrePagos: boolean("creditos_pre_pagos").notNull().default(false),
  pacotesCreditos: jsonb("pacotes_creditos")
    .$type<{ allowCustom: boolean; stepEnabled: boolean; step: number }>()
    .notNull().default({ allowCustom: true, stepEnabled: true, step: 10 }),
  // Legado/compatibilidade: sempre true. Repasses Asaas são obrigatórios; não controla regra de negócio.
  splitPagamento: boolean("split_pagamento").notNull().default(true),
  // Módulo Nota Fiscal (Bling): habilita emissão manual de NFS-e por compra de crédito
  nfeAtivado: boolean("nfe_ativado").notNull().default(false),
  // Módulo Detran Veículo/Foto: fotos do veículo + exigência para atribuir TV
  detranVeiculoFoto: boolean("detran_veiculo_foto").notNull().default(false),
  // Chave PIX para receber o split automático via Asaas (obrigatória quando splitPagamento=true)
  chavePix: text("chave_pix"),
  // Como gerar fatura para o parceiro: 'por_laudo' | 'acumular_x' | 'por_lote'
  modeFaturamento: text("mode_faturamento").notNull().default("por_laudo"),
  // Quantidade de laudos para acumular antes de gerar fatura (usado quando modeFaturamento = 'acumular_x')
  faturamentoAcumularX: integer("faturamento_acumular_x").notNull().default(1),
  // Como agrupar laudos em lotes: 'geral' | 'por_parceiro'
  modeLote: text("mode_lote").notNull().default("geral"),
  // Inadimplência: true = bloqueia imediatamente ao vencer; false = usa período de atraso
  inadimplenciaBloqueioImediato: boolean("inadimplencia_bloqueio_imediato").notNull().default(false),
  // Dias de atraso até o status "atrasada" (só usado quando inadimplenciaBloqueioImediato=false)
  inadimplenciaDiasAtraso: integer("inadimplencia_dias_atraso").notNull().default(1),
  // Dias de atraso até o status "bloqueada"
  inadimplenciaDiasBloqueio: integer("inadimplencia_dias_bloqueio").notNull().default(30),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// Preços da vistoriadora para seus parceiros (preço que ela cobra por laudo)
// parceiroId null = preço padrão para todos os parceiros
// parceiroId preenchido = preço específico para aquele parceiro
export const configPrecosVistoriadora = pgTable(
  "config_precos_vistoriadora",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    vistoriadoraId: uuid("vistoriadora_id")
      .notNull()
      .references(() => empresas.id, { onDelete: "cascade" }),
    parceiroId: uuid("parceiro_id").references(() => empresas.id, {
      onDelete: "cascade",
    }),
    // valorPorLaudo = preço flat (fallback); valoresPorTipo = preço por tipo de veículo
    valorPorLaudo: numeric("valor_por_laudo", { precision: 10, scale: 2 }),
    valoresPorTipo: jsonb("valores_por_tipo").$type<Record<string, number>>(),
    criadoEm: timestamp("criado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
    atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
);

// Ficha Técnica de Elaboração: até 2 registros por vistoriadora (RT + Admin)
// Aparece no rodapé final do laudo PDF
export const fichaTecnicaElaboracao = pgTable("ficha_tecnica_elaboracao", {
  id: uuid("id").primaryKey().defaultRandom(),
  vistoriadoraId: uuid("vistoriadora_id")
    .references(() => empresas.id, { onDelete: "cascade" })
    .notNull(),
  userId: uuid("user_id")
    .references(() => userProfiles.id, { onDelete: "cascade" })
    .notNull(),
  especialidade: text("especialidade").notNull(),
  documentoFiscal: text("documento_fiscal"),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const empresasRelations = relations(empresas, ({ one, many }) => ({
  vistoriadora: one(empresas, {
    fields: [empresas.vistoriadoraId],
    references: [empresas.id],
    relationName: "vistoriadoraParceiros",
  }),
  parceiros: many(empresas, { relationName: "vistoriadoraParceiros" }),
  usuarios: many(userProfiles),
  inviteCodes: many(inviteCodes),
  laudosComoVistoriadora: many(laudos, { relationName: "laudoVistoriadora" }),
  laudosComoParceiro: many(laudos, { relationName: "laudoParceiro" }),
  lotes: many(lotes),
  faturas: many(faturas),
  lancamentos: many(lancamentos),
}));

export const userProfilesRelations = relations(userProfiles, ({ one }) => ({
  empresa: one(empresas, {
    fields: [userProfiles.empresaId],
    references: [empresas.id],
  }),
}));

export const inviteCodesRelations = relations(inviteCodes, ({ one }) => ({
  vistoriadora: one(empresas, {
    fields: [inviteCodes.vistoriadoraId],
    references: [empresas.id],
  }),
}));

export const laudosRelations = relations(laudos, ({ one, many }) => ({
  vistoriadora: one(empresas, {
    fields: [laudos.vistoriadoraId],
    references: [empresas.id],
    relationName: "laudoVistoriadora",
  }),
  parceiro: one(empresas, {
    fields: [laudos.parceiroId],
    references: [empresas.id],
    relationName: "laudoParceiro",
  }),
  tecnicoVistoria: one(userProfiles, {
    fields: [laudos.tecnicoVistoriaId],
    references: [userProfiles.id],
  }),
  responsavelTecnico: one(userProfiles, {
    fields: [laudos.responsavelTecnicoId],
    references: [userProfiles.id],
  }),
  criadoPorUser: one(userProfiles, {
    fields: [laudos.criadoPor],
    references: [userProfiles.id],
  }),
  veiculo: one(veiculos, {
    fields: [laudos.veiculoId],
    references: [veiculos.id],
  }),
  laudoPecas: many(laudoPecas),
}));

export const laudoPecasRelations = relations(laudoPecas, ({ one, many }) => ({
  laudo: one(laudos, {
    fields: [laudoPecas.laudoId],
    references: [laudos.id],
  }),
  catalogoPeca: one(pecas, {
    fields: [laudoPecas.catalogoPecaId],
    references: [pecas.id],
  }),
  respostas: many(laudoPecaRespostas),
}));

export const laudoPecaRespostasRelations = relations(
  laudoPecaRespostas,
  ({ one }) => ({
    laudoPeca: one(laudoPecas, {
      fields: [laudoPecaRespostas.laudoPecaId],
      references: [laudoPecas.id],
    }),
    pergunta: one(perguntas, {
      fields: [laudoPecaRespostas.perguntaId],
      references: [perguntas.id],
    }),
  }),
);

export const veiculosRelations = relations(veiculos, ({ one, many }) => ({
  parceiro: one(empresas, {
    fields: [veiculos.parceiroId],
    references: [empresas.id],
  }),
  criadoPorUser: one(userProfiles, {
    fields: [veiculos.criadoPor],
    references: [userProfiles.id],
  }),
  laudos: many(laudos),
  fotos: many(veiculoFotos),
}));

export const veiculoFotosRelations = relations(veiculoFotos, ({ one }) => ({
  veiculo: one(veiculos, { fields: [veiculoFotos.veiculoId], references: [veiculos.id] }),
}));

export const lotesRelations = relations(lotes, ({ one }) => ({
  vistoriadora: one(empresas, {
    fields: [lotes.vistoriadoraId],
    references: [empresas.id],
  }),
  parceiro: one(empresas, {
    fields: [lotes.parceiroId],
    references: [empresas.id],
    relationName: "loteParceiro",
  }),
}));

export const configPrecosVistoriadoraRelations = relations(
  configPrecosVistoriadora,
  ({ one }) => ({
    vistoriadora: one(empresas, {
      fields: [configPrecosVistoriadora.vistoriadoraId],
      references: [empresas.id],
    }),
    parceiro: one(empresas, {
      fields: [configPrecosVistoriadora.parceiroId],
      references: [empresas.id],
    }),
  }),
);

export const perguntasRelations = relations(perguntas, ({ many }) => ({
  pecaPerguntas: many(pecaPerguntas),
  laudoPecaRespostas: many(laudoPecaRespostas),
}));

export const pecaPerguntasRelations = relations(pecaPerguntas, ({ one }) => ({
  peca: one(pecas, {
    fields: [pecaPerguntas.pecaId],
    references: [pecas.id],
  }),
  pergunta: one(perguntas, {
    fields: [pecaPerguntas.perguntaId],
    references: [perguntas.id],
  }),
}));

export const faturasRelations = relations(faturas, ({ one, many }) => ({
  vistoriadora: one(empresas, {
    fields: [faturas.vistoriadoraId],
    references: [empresas.id],
  }),
  lote: one(lotes, {
    fields: [faturas.loteId],
    references: [lotes.id],
  }),
  criadoPorUser: one(userProfiles, {
    fields: [faturas.criadoPor],
    references: [userProfiles.id],
  }),
  itens: many(faturaItens),
}));

export const faturaItensRelations = relations(faturaItens, ({ one }) => ({
  fatura: one(faturas, {
    fields: [faturaItens.faturaId],
    references: [faturas.id],
  }),
}));

export const lancamentosRelations = relations(lancamentos, ({ one }) => ({
  vistoriadora: one(empresas, {
    fields: [lancamentos.vistoriadoraId],
    references: [empresas.id],
  }),
  criadoPorUser: one(userProfiles, {
    fields: [lancamentos.criadoPor],
    references: [userProfiles.id],
  }),
}));

export const configuracoesVistoriadoraRelations = relations(
  configuracoesVistoriadora,
  ({ one }) => ({
    vistoriadora: one(empresas, {
      fields: [configuracoesVistoriadora.vistoriadoraId],
      references: [empresas.id],
    }),
  }),
);

export const fichaTecnicaElaboracaoRelations = relations(
  fichaTecnicaElaboracao,
  ({ one }) => ({
    vistoriadora: one(empresas, {
      fields: [fichaTecnicaElaboracao.vistoriadoraId],
      references: [empresas.id],
    }),
    usuario: one(userProfiles, {
      fields: [fichaTecnicaElaboracao.userId],
      references: [userProfiles.id],
    }),
  }),
);

// Faturas emitidas pela Vistoriadora para o Parceiro de Negócios (só controle de cobrança)
export const faturasParceiro = pgTable("faturas_parceiro", {
  id: uuid("id").primaryKey().defaultRandom(),
  vistoriadoraId: uuid("vistoriadora_id").references(() => empresas.id, {
    onDelete: "set null",
  }),
  parceiroId: uuid("parceiro_id").references(() => empresas.id, {
    onDelete: "set null",
  }),
  numero: integer("numero").notNull(), // Sequência numérica por ano (para queries/ordenação)
  numeroFatura: text("numero_fatura").notNull().unique(), // Formato: FAT-LLEGAL-{tag}-{seq}
  mes: smallint("mes").notNull(),
  ano: smallint("ano").notNull(),
  status: text("status").notNull().default("pendente"), // pendente | pago | cancelado | atrasada | bloqueada
  valorTotal: numeric("valor_total", { precision: 10, scale: 2 }).notNull(),
  diasAtraso: integer("dias_atraso").notNull().default(0),
  juros: numeric("juros", { precision: 10, scale: 2 }).notNull().default("0"),
  multa: numeric("multa", { precision: 10, scale: 2 }).notNull().default("0"),
  vencimento: date("vencimento").notNull(),
  observacoes: text("observacoes"),
  // Chave PIX da vistoriadora no momento da emissão (snapshot — não muda se a chave for alterada depois)
  chavePix: text("chave_pix"),
  pagoEm: timestamp("pago_em", { withTimezone: true }),
  asaasCobrancaId: text("asaas_cobranca_id"), // Rastreia cobrança Asaas para pós-pago
  criadoPor: uuid("criado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const faturaParceiroItens = pgTable("fatura_parceiro_itens", {
  id: uuid("id").primaryKey().defaultRandom(),
  faturaParceiroId: uuid("fatura_parceiro_id")
    .notNull()
    .references(() => faturasParceiro.id, { onDelete: "cascade" }),
  laudoId: uuid("laudo_id").references(() => laudos.id, {
    onDelete: "set null",
  }),
  laudoIdTexto: text("laudo_id_texto"),
  placa: text("placa"),
  tipoVeiculo: text("tipo_veiculo").notNull(),
  valorUnitario: numeric("valor_unitario", {
    precision: 10,
    scale: 2,
  }).notNull(),
  valorPlataformaUnitario: numeric("valor_plataforma_unitario", { precision: 10, scale: 2 }),
  valorVistoriadoraUnitario: numeric("valor_vistoriadora_unitario", { precision: 10, scale: 2 }),
  descricao: text("descricao"),
  createdAt: timestamp("created_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const faturasParceiroRelations = relations(
  faturasParceiro,
  ({ one, many }) => ({
    vistoriadora: one(empresas, {
      fields: [faturasParceiro.vistoriadoraId],
      references: [empresas.id],
      relationName: "faturasParceiroVistoriadora",
    }),
    parceiro: one(empresas, {
      fields: [faturasParceiro.parceiroId],
      references: [empresas.id],
      relationName: "faturasParceiroCliente",
    }),
    criadoPorUser: one(userProfiles, {
      fields: [faturasParceiro.criadoPor],
      references: [userProfiles.id],
    }),
    itens: many(faturaParceiroItens),
  }),
);

export const faturaParceiroItensRelations = relations(
  faturaParceiroItens,
  ({ one }) => ({
    faturaParceiro: one(faturasParceiro, {
      fields: [faturaParceiroItens.faturaParceiroId],
      references: [faturasParceiro.id],
    }),
    laudo: one(laudos, {
      fields: [faturaParceiroItens.laudoId],
      references: [laudos.id],
    }),
  }),
);

export const insertFaturaParceiroSchema = createInsertSchema(
  faturasParceiro,
).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export const insertFaturaParceiroItemSchema = createInsertSchema(
  faturaParceiroItens,
).omit({
  id: true,
  createdAt: true,
});

export type FaturaParceiro = typeof faturasParceiro.$inferSelect;
export type FaturaParceiroItem = typeof faturaParceiroItens.$inferSelect;
export type InsertFaturaParceiro = z.infer<typeof insertFaturaParceiroSchema>;
export type InsertFaturaParceiroItem = z.infer<
  typeof insertFaturaParceiroItemSchema
>;

// ---------------------------------------------------------------------------
// Split Transferências — rastreia cada PIX automático enviado à vistoriadora
// ---------------------------------------------------------------------------
export const splitTransferencias = pgTable("split_transferencias", {
  id: uuid("id").primaryKey().defaultRandom(),
  compraId: uuid("compra_id"), // Pré-pago: referencia compras_credito
  faturaParceiroId: uuid("fatura_parceiro_id"), // Pós-pago: referencia faturas_parceiro
  vistoriadoraId: uuid("vistoriadora_id")
    .notNull()
    .references(() => empresas.id, { onDelete: "restrict" }),
  chavePix: text("chave_pix").notNull(),
  valorTotal: numeric("valor_total", { precision: 10, scale: 2 }).notNull(),
  valorVistoriadora: numeric("valor_vistoriadora", {
    precision: 10,
    scale: 2,
  }).notNull(),
  valorOwner: numeric("valor_owner", { precision: 10, scale: 2 }).notNull(),
  percentual: numeric("percentual", { precision: 5, scale: 2 }).notNull(),
  asaasTransferId: text("asaas_transfer_id"),
  status: text("status").notNull().default("pendente"),
  erro: text("erro"),
  tentativas: integer("tentativas").notNull().default(0),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const splitTransferenciasRelations = relations(
  splitTransferencias,
  ({ one }) => ({
    vistoriadora: one(empresas, {
      fields: [splitTransferencias.vistoriadoraId],
      references: [empresas.id],
    }),
  }),
);

export const insertSplitTransferenciaSchema = createInsertSchema(
  splitTransferencias,
).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export type SplitTransferencia = typeof splitTransferencias.$inferSelect;
export type InsertSplitTransferencia = z.infer<
  typeof insertSplitTransferenciaSchema
>;

// ---------------------------------------------------------------------------
// Integração Bling — conexão OAuth2 por empresa (Vistoriadora ou a empresa OWNER)
// usada para emitir NFS-e de compras de crédito
// ---------------------------------------------------------------------------
export const blingConnections = pgTable("bling_connections", {
  id: uuid("id").primaryKey().defaultRandom(),
  empresaId: uuid("empresa_id")
    .notNull()
    .unique()
    .references(() => empresas.id, { onDelete: "cascade" }),
  accessTokenEnc: text("access_token_enc").notNull(),
  refreshTokenEnc: text("refresh_token_enc").notNull(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  // Código do serviço cadastrado no Bling para "vistoria/laudo" (preenchido manualmente)
  servicoIdPadrao: text("servico_id_padrao"),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const insertBlingConnectionSchema = createInsertSchema(
  blingConnections,
).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export type BlingConnection = typeof blingConnections.$inferSelect;
export type InsertBlingConnection = z.infer<
  typeof insertBlingConnectionSchema
>;

// ---------------------------------------------------------------------------
// NF-e (NFS-e) emitidas via Bling — duas por compra de crédito:
// "vistoriadora_parceiro" (Vistoriadora cobra Parceiro) e
// "owner_parceiro" (Plataforma cobra Parceiro; legado owner_vistoriadora continua consultável)
// ---------------------------------------------------------------------------
export const nfeEmissoes = pgTable(
  "nfe_emissoes",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    // Pré-pago: compra em compras_credito (Supabase, fora do Drizzle)
    compraId: text("compra_id"),
    // Pós-pago: referências às faturas (origem da emissão)
    faturaParceiroId: uuid("fatura_parceiro_id").references(
      () => faturasParceiro.id,
      { onDelete: "set null" }
    ),
    faturaId: uuid("fatura_id").references(() => faturas.id, {
      onDelete: "set null",
    }),
    tipo: text("tipo").notNull(), // vistoriadora_parceiro | owner_parceiro | owner_vistoriadora (legado)
    empresaEmissoraId: uuid("empresa_emissora_id")
      .notNull()
      .references(() => empresas.id, { onDelete: "restrict" }),
    empresaTomadoraId: uuid("empresa_tomadora_id")
      .notNull()
      .references(() => empresas.id, { onDelete: "restrict" }),
    valor: numeric("valor", { precision: 10, scale: 2 }).notNull(),
    status: text("status").notNull().default("pendente"), // pendente | emitida | erro | cancelada
    blingNotaId: text("bling_nota_id"),
    numeroNota: text("numero_nota"),
    linkPdf: text("link_pdf"),
    // Gateway que efetivamente emitiu a nota: 'bling' (padrão, sempre disponível) ou
    // 'asaas' (só a nota owner_vistoriadora tenta; cai pro bling se falhar/indisponível).
    gateway: text("gateway").notNull().default("bling"),
    asaasInvoiceId: text("asaas_invoice_id"),
    // Repasses Asaas: nota da Plataforma = serviço + taxa de processamento (check no banco)
    asaasCotacaoId: uuid("asaas_cotacao_id"),
    valorServico: numeric("valor_servico", { precision: 10, scale: 2 }),
    valorTaxaProcessamento: numeric("valor_taxa_processamento", { precision: 10, scale: 2 }),
    erro: text("erro"),
    criadoPor: uuid("criado_por").references(() => userProfiles.id),
    criadoEm: timestamp("criado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
    atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    // Pré-pago: uma nota por compra+tipo
    unique().on(table.compraId, table.tipo),
    // Pós-pago: uma nota por fatura+tipo (índices criados na migration)
  ],
);

export const nfeEmissoesRelations = relations(nfeEmissoes, ({ one }) => ({
  empresaEmissora: one(empresas, {
    fields: [nfeEmissoes.empresaEmissoraId],
    references: [empresas.id],
    relationName: "nfeEmissoesEmissora",
  }),
  empresaTomadora: one(empresas, {
    fields: [nfeEmissoes.empresaTomadoraId],
    references: [empresas.id],
    relationName: "nfeEmissoesTomadora",
  }),
  criadoPorUser: one(userProfiles, {
    fields: [nfeEmissoes.criadoPor],
    references: [userProfiles.id],
  }),
}));

export const insertNfeEmissaoSchema = createInsertSchema(nfeEmissoes).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export type NfeEmissao = typeof nfeEmissoes.$inferSelect;
export type InsertNfeEmissao = z.infer<typeof insertNfeEmissaoSchema>;

// ---------------------------------------------------------------------------
// Repasses Asaas (migrations/asaas_repasses_wallet.sql é a fonte de verdade de
// constraints, índices, triggers e RLS; aqui só o espelho usado pelo servidor).
// ambiente: 'producao' | 'sandbox' | 'simulado'; modo_teste = ambiente <> 'producao'.
// wallet_id só existe em asaas_subcontas (e snapshot interno em repasse_lotes):
// nunca devolver essas colunas em DTO do Parceiro. Nenhuma API key é persistida.
// ---------------------------------------------------------------------------
const ambienteCols = {
  ambiente: text("ambiente").notNull(),
  modoTeste: boolean("modo_teste").notNull(),
};
const dinheiro = (nome: string) => numeric(nome, { precision: 12, scale: 2 });
const criadoAtualizado = {
  criadoEm: timestamp("criado_em", { withTimezone: true }).notNull().defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }).notNull().defaultNow(),
};

export const asaasTaxasSnapshots = pgTable("asaas_taxas_snapshots", {
  id: uuid("id").primaryKey().defaultRandom(),
  ...ambienteCols,
  origem: text("origem").notNull(), // api | fallback
  obtidoEm: timestamp("obtido_em", { withTimezone: true }).notNull().defaultNow(),
  validoAte: timestamp("valido_ate", { withTimezone: true }).notNull(),
  promocaoExpiraEm: timestamp("promocao_expira_em", { withTimezone: true }),
  payload: jsonb("payload").notNull(),
  hash: text("hash").notNull(),
  criadoEm: timestamp("criado_em", { withTimezone: true }).notNull().defaultNow(),
});

export const asaasCotacoes = pgTable("asaas_cotacoes", {
  id: uuid("id").primaryKey().defaultRandom(),
  ...ambienteCols,
  origemTipo: text("origem_tipo").notNull(), // compra_credito | fatura_parceiro
  origemId: uuid("origem_id").notNull(),
  empresaPagadoraId: uuid("empresa_pagadora_id").notNull().references(() => empresas.id),
  vistoriadoraId: uuid("vistoriadora_id").notNull().references(() => empresas.id),
  metodo: text("metodo").notNull(), // pix | boleto | cartao_credito | cartao_debito
  parcelas: smallint("parcelas").notNull().default(1),
  baseOwner: dinheiro("base_owner").notNull(),
  baseVistoriadora: dinheiro("base_vistoriadora").notNull(),
  taxaPrevista: dinheiro("taxa_prevista").notNull(),
  taxaEfetiva: dinheiro("taxa_efetiva"),
  total: dinheiro("total").notNull(),
  itens: jsonb("itens").notNull().default([]),
  usandoFallback: boolean("usando_fallback").notNull().default(false),
  netValue: dinheiro("net_value"),
  snapshotId: uuid("snapshot_id").notNull().references(() => asaasTaxasSnapshots.id),
  status: text("status").notNull().default("aberta"), // aberta | cobranca_criada | paga | expirada | cancelada | divergente
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  paymentId: text("payment_id"),
  externalReference: text("external_reference"),
  checkoutUrl: text("checkout_url"),
  ...criadoAtualizado,
});

export const asaasSubcontas = pgTable("asaas_subcontas", {
  id: uuid("id").primaryKey().defaultRandom(),
  vistoriadoraId: uuid("vistoriadora_id").notNull().references(() => empresas.id),
  ...ambienteCols,
  accountId: text("account_id"),
  walletId: text("wallet_id"),
  // nao_iniciada | aguardando_documentos | em_analise | aprovada | rejeitada | dados_expirando | dados_expirados
  status: text("status").notNull().default("nao_iniciada"),
  agendaRepasse: text("agenda_repasse").notNull().default("diario"), // diario | semanal | mensal | manual
  lastStatusAt: timestamp("last_status_at", { withTimezone: true }),
  expiracaoAnualProxima: boolean("expiracao_anual_proxima").notNull().default(false),
  expiracaoAnualVencida: boolean("expiracao_anual_vencida").notNull().default(false),
  ...criadoAtualizado,
});

export const asaasWebhookEventos = pgTable("asaas_webhook_eventos", {
  id: uuid("id").primaryKey().defaultRandom(),
  ...ambienteCols,
  asaasEventId: text("asaas_event_id").notNull(),
  tipo: text("tipo").notNull(),
  payload: jsonb("payload").notNull(), // já sanitizado
  status: text("status").notNull().default("recebido"), // recebido | processando | processado | erro | ignorado
  tentativas: integer("tentativas").notNull().default(0),
  erro: text("erro"),
  recebidoEm: timestamp("recebido_em", { withTimezone: true }).notNull().defaultNow(),
  processadoEm: timestamp("processado_em", { withTimezone: true }),
});

export const asaasCompensacoes = pgTable("asaas_compensacoes", {
  id: uuid("id").primaryKey().defaultRandom(),
  ...ambienteCols,
  parceiroId: uuid("parceiro_id").notNull().references(() => empresas.id),
  cotacaoOrigemId: uuid("cotacao_origem_id").notNull().references(() => asaasCotacoes.id),
  valor: dinheiro("valor").notNull(),
  status: text("status").notNull().default("disponivel"), // disponivel | aplicada | cancelada
  cotacaoAplicacaoId: uuid("cotacao_aplicacao_id").references(() => asaasCotacoes.id),
  ...criadoAtualizado,
});

export const asaasTransferenciasConta = pgTable("asaas_transferencias_conta", {
  id: uuid("id").primaryKey().defaultRandom(),
  ...ambienteCols,
  asaasTransferId: text("asaas_transfer_id").notNull(),
  origem: text("origem").notNull(), // plataforma | externa
  operacao: text("operacao").notNull(), // wallet_interna | pix | ted
  status: text("status").notNull(),
  valor: dinheiro("valor").notNull(),
  tarifa: dinheiro("tarifa").notNull().default("0"),
  competencia: date("competencia").notNull(), // 1º dia do mês
  concluidaEm: timestamp("concluida_em", { withTimezone: true }),
  reconciliadoEm: timestamp("reconciliado_em", { withTimezone: true }),
  ...criadoAtualizado,
});

export const creditoLotes = pgTable("credito_lotes", {
  id: uuid("id").primaryKey().defaultRandom(),
  ...ambienteCols,
  compraId: uuid("compra_id").notNull(), // compras_credito (Supabase, fora do Drizzle)
  parceiroId: uuid("parceiro_id").notNull().references(() => empresas.id),
  vistoriadoraId: uuid("vistoriadora_id").notNull().references(() => empresas.id),
  tipoVeiculo: tipoVeiculoEnum("tipo_veiculo").notNull(),
  quantidadeComprada: integer("quantidade_comprada").notNull(),
  quantidadeReservada: integer("quantidade_reservada").notNull().default(0),
  quantidadeConsumida: integer("quantidade_consumida").notNull().default(0),
  quantidadeDisponivel: integer("quantidade_disponivel").generatedAlwaysAs(
    sql`quantidade_comprada - quantidade_reservada - quantidade_consumida`,
  ),
  precoPlataformaUnitario: numeric("preco_plataforma_unitario", { precision: 10, scale: 2 }).notNull(),
  precoVistoriadoraUnitario: numeric("preco_vistoriadora_unitario", { precision: 10, scale: 2 }).notNull(),
  cotacaoId: uuid("cotacao_id").references(() => asaasCotacoes.id),
  ...criadoAtualizado,
});

export const repasseLotes = pgTable("repasse_lotes", {
  id: uuid("id").primaryKey().defaultRandom(),
  vistoriadoraId: uuid("vistoriadora_id").notNull().references(() => empresas.id),
  ...ambienteCols,
  // cron_diario | cron_semanal | cron_mensal | admin | owner | pix_emergencial | teste
  origemExecucao: text("origem_execucao").notNull(),
  valor: dinheiro("valor").notNull().default("0"),
  quantidadeItens: integer("quantidade_itens").notNull().default(0),
  walletIdSnapshot: text("wallet_id_snapshot"), // interno: nunca em DTO público
  asaasTransferId: text("asaas_transfer_id"),
  externalReference: text("external_reference"),
  // criando | pendente_asaas | processando | bloqueado | concluido | falhou | cancelado | simulado_concluido
  status: text("status").notNull().default("criando"),
  erro: text("erro"),
  comprovante: text("comprovante"),
  linhasOrigem: uuid("linhas_origem").array(), // ids do ledger devolvidos quando o lote falhou/cancelou (base do PIX emergencial)
  pixEmergencialDe: uuid("pix_emergencial_de"), // lote de origem do PIX emergencial (um ativo/concluído por origem)
  iniciadoPor: uuid("iniciado_por").references(() => userProfiles.id),
  ...criadoAtualizado,
});

// Append-only (trigger): só status/repasse_lote_id/eligible_at mudam, pelas transições do spec §9.
export const creditoRepasseLedger = pgTable("credito_repasse_ledger", {
  id: uuid("id").primaryKey().defaultRandom(),
  ...ambienteCols,
  // pre_pago_consumo | pos_pago_item | ajuste_estorno | ajuste_chargeback | ajuste_manual_auditado | gratuito
  origem: text("origem").notNull(),
  origemId: uuid("origem_id").notNull(),
  laudoId: uuid("laudo_id").references(() => laudos.id),
  faturaItemId: uuid("fatura_item_id"),
  creditoLoteId: uuid("credito_lote_id").references(() => creditoLotes.id),
  vistoriadoraId: uuid("vistoriadora_id").notNull().references(() => empresas.id),
  parceiroId: uuid("parceiro_id").references(() => empresas.id),
  valor: dinheiro("valor").notNull(),
  // aguardando_pagamento | pendente | em_lote | transferido | cancelado | dispensado
  status: text("status").notNull(),
  eligibleAt: timestamp("eligible_at", { withTimezone: true }),
  repasseLoteId: uuid("repasse_lote_id").references(() => repasseLotes.id),
  sequenciaAjuste: integer("sequencia_ajuste").notNull().default(0),
  ...criadoAtualizado,
});

const semIdDatas = { id: true, criadoEm: true, atualizadoEm: true } as const;
export const insertAsaasTaxasSnapshotSchema = createInsertSchema(asaasTaxasSnapshots).omit({ id: true, criadoEm: true });
export const insertAsaasCotacaoSchema = createInsertSchema(asaasCotacoes).omit(semIdDatas);
export const insertAsaasSubcontaSchema = createInsertSchema(asaasSubcontas).omit(semIdDatas);
export const insertAsaasWebhookEventoSchema = createInsertSchema(asaasWebhookEventos).omit({ id: true, recebidoEm: true });
export const insertAsaasCompensacaoSchema = createInsertSchema(asaasCompensacoes).omit(semIdDatas);
export const insertAsaasTransferenciaContaSchema = createInsertSchema(asaasTransferenciasConta).omit(semIdDatas);
export const insertCreditoLoteSchema = createInsertSchema(creditoLotes).omit(semIdDatas);
export const insertRepasseLoteSchema = createInsertSchema(repasseLotes).omit(semIdDatas);
export const insertCreditoRepasseLedgerSchema = createInsertSchema(creditoRepasseLedger).omit(semIdDatas);

export type AsaasTaxasSnapshot = typeof asaasTaxasSnapshots.$inferSelect;
export type AsaasCotacao = typeof asaasCotacoes.$inferSelect;
export type AsaasSubconta = typeof asaasSubcontas.$inferSelect;
export type AsaasWebhookEvento = typeof asaasWebhookEventos.$inferSelect;
export type AsaasCompensacao = typeof asaasCompensacoes.$inferSelect;
export type AsaasTransferenciaConta = typeof asaasTransferenciasConta.$inferSelect;
export type CreditoLote = typeof creditoLotes.$inferSelect;
export type RepasseLote = typeof repasseLotes.$inferSelect;
export type CreditoRepasseLedger = typeof creditoRepasseLedger.$inferSelect;
export type InsertAsaasTaxasSnapshot = z.infer<typeof insertAsaasTaxasSnapshotSchema>;
export type InsertAsaasCotacao = z.infer<typeof insertAsaasCotacaoSchema>;
export type InsertAsaasSubconta = z.infer<typeof insertAsaasSubcontaSchema>;
export type InsertAsaasWebhookEvento = z.infer<typeof insertAsaasWebhookEventoSchema>;
export type InsertAsaasCompensacao = z.infer<typeof insertAsaasCompensacaoSchema>;
export type InsertAsaasTransferenciaConta = z.infer<typeof insertAsaasTransferenciaContaSchema>;
export type InsertCreditoLote = z.infer<typeof insertCreditoLoteSchema>;
export type InsertRepasseLote = z.infer<typeof insertRepasseLoteSchema>;
export type InsertCreditoRepasseLedger = z.infer<typeof insertCreditoRepasseLedgerSchema>;

// ---------------------------------------------------------------------------
// Email Logs — auditoria de envios de email
// ---------------------------------------------------------------------------
export const emailLogs = pgTable("email_logs", {
  id: uuid("id").primaryKey().defaultRandom(),
  tipo: text("tipo").notNull(),
  destinatarioEmail: text("destinatario_email").notNull(),
  refType: text("ref_type"),
  refId: uuid("ref_id"),
  status: text("status").notNull(), // "enviado" | "erro"
  erro: text("erro"),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// ---------------------------------------------------------------------------
// Solicitações de Laudo — parceiro solicita autorização quando bloqueado pelo limite
// ---------------------------------------------------------------------------
export const solicitacoesLaudo = pgTable("solicitacoes_laudo", {
  id: uuid("id").primaryKey().defaultRandom(),
  parceiroId: uuid("parceiro_id")
    .notNull()
    .references(() => empresas.id, { onDelete: "cascade" }),
  vistoriadoraId: uuid("vistoriadora_id")
    .notNull()
    .references(() => empresas.id, { onDelete: "cascade" }),
  solicitadoPor: uuid("solicitado_por").notNull(),
  mes: integer("mes").notNull(),
  ano: integer("ano").notNull(),
  status: text("status").notNull().default("pendente"),
  aprovadoPor: uuid("aprovado_por"),
  laudoId: uuid("laudo_id").references(() => laudos.id, {
    onDelete: "set null",
  }),
  motivoRejeicao: text("motivo_rejeicao"),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const solicitacoesLaudoRelations = relations(
  solicitacoesLaudo,
  ({ one }) => ({
    parceiro: one(empresas, {
      fields: [solicitacoesLaudo.parceiroId],
      references: [empresas.id],
      relationName: "solicitacoesParceiro",
    }),
    vistoriadora: one(empresas, {
      fields: [solicitacoesLaudo.vistoriadoraId],
      references: [empresas.id],
      relationName: "solicitacoesVistoriadora",
    }),
    laudo: one(laudos, {
      fields: [solicitacoesLaudo.laudoId],
      references: [laudos.id],
    }),
  }),
);

export const insertSolicitacaoLaudoSchema = createInsertSchema(
  solicitacoesLaudo,
).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export type SolicitacaoLaudo = typeof solicitacoesLaudo.$inferSelect;
export type InsertSolicitacaoLaudo = z.infer<
  typeof insertSolicitacaoLaudoSchema
>;

export type FaturaParceiroWithDetails = FaturaParceiro & {
  vistoriadora?: Empresa;
  parceiro?: Empresa;
  itens?: FaturaParceiroItem[];
};

export const notificacoes = pgTable("notificacoes", {
  id: uuid("id").defaultRandom().primaryKey(),
  usuarioId: uuid("usuario_id")
    .notNull()
    .references(() => userProfiles.id, { onDelete: "cascade" }),
  empresaId: uuid("empresa_id")
    .notNull()
    .references(() => empresas.id, { onDelete: "cascade" }),
  tipo: text("tipo").notNull(),
  titulo: text("titulo").notNull(),
  mensagem: text("mensagem").notNull(),
  laudoDbId: uuid("laudo_db_id").references(() => laudos.id, {
    onDelete: "set null",
  }),
  laudoId: text("laudo_id"),
  lida: boolean("lida").default(false).notNull(),
  criadaEm: timestamp("criada_em", { withTimezone: true })
    .defaultNow()
    .notNull(),
});

export const notificacoesRelations = relations(notificacoes, ({ one }) => ({
  usuario: one(userProfiles, {
    fields: [notificacoes.usuarioId],
    references: [userProfiles.id],
  }),
  empresa: one(empresas, {
    fields: [notificacoes.empresaId],
    references: [empresas.id],
  }),
  laudo: one(laudos, {
    fields: [notificacoes.laudoDbId],
    references: [laudos.id],
  }),
}));

export type Notificacao = typeof notificacoes.$inferSelect;
export type InsertNotificacao = typeof notificacoes.$inferInsert;

// ─── ARTs dos Responsáveis Técnicos ──────────────────────────────────────────

export const rtArts = pgTable("rt_arts", {
  id: uuid("id").primaryKey().defaultRandom(),
  rtId: uuid("rt_id")
    .notNull()
    .references(() => userProfiles.id, { onDelete: "cascade" }),
  numeroArt: text("numero_art").notNull(),
  descricao: text("descricao"),
  dataInicio: date("data_inicio").notNull(),
  dataFim: date("data_fim"),
  status: text("status").notNull().default("ativa"), // "ativa" | "encerrada"
  criadoPor: uuid("criado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const rtArtsRelations = relations(rtArts, ({ one }) => ({
  rt: one(userProfiles, {
    fields: [rtArts.rtId],
    references: [userProfiles.id],
  }),
  criadoPorUser: one(userProfiles, {
    fields: [rtArts.criadoPor],
    references: [userProfiles.id],
  }),
}));

export const insertRtArtSchema = createInsertSchema(rtArts).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});

export type RtArt = typeof rtArts.$inferSelect;
export type InsertRtArt = z.infer<typeof insertRtArtSchema>;

// ─── Certificados Digitais (ICP-Brasil) dos RTs ─────────────────────────────

export const rtCertificados = pgTable("rt_certificados", {
  id: uuid("id").primaryKey().defaultRandom(),
  empresaId: uuid("empresa_id")
    .references(() => empresas.id, { onDelete: "cascade" })
    .notNull(),
  rtUserId: uuid("rt_user_id")
    .references(() => userProfiles.id, { onDelete: "cascade" })
    .notNull()
    .unique(),
  certificadoStoragePath: text("certificado_storage_path").notNull(),
  senhaEncriptada: text("senha_encriptada").notNull(),
  nomeArquivo: text("nome_arquivo"),
  criadoPor: uuid("criado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const rtCertificadosRelations = relations(rtCertificados, ({ one }) => ({
  empresa: one(empresas, {
    fields: [rtCertificados.empresaId],
    references: [empresas.id],
  }),
  rtUser: one(userProfiles, {
    fields: [rtCertificados.rtUserId],
    references: [userProfiles.id],
  }),
}));

export type RtCertificado = typeof rtCertificados.$inferSelect;
export type InsertRtCertificado = typeof rtCertificados.$inferInsert;

export const apks = pgTable("apks", {
  id: uuid("id").primaryKey().defaultRandom(),
  versao: text("versao").notNull(),
  nomeArquivo: text("nome_arquivo").notNull(),
  storagePath: text("storage_path").notNull(),
  tamanhoBytes: integer("tamanho_bytes").notNull(),
  principal: boolean("principal").notNull().default(false),
  enviadoPor: uuid("enviado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export type Apk = typeof apks.$inferSelect;
export type InsertApk = typeof apks.$inferInsert;

export const tutorialPdfs = pgTable("tutorial_pdfs", {
  id: uuid("id").primaryKey().defaultRandom(),
  papel: text("papel").notNull(),
  versao: integer("versao").notNull(),
  nomeArquivo: text("nome_arquivo").notNull(),
  storagePath: text("storage_path").notNull(),
  tamanhoBytes: integer("tamanho_bytes").notNull(),
  principal: boolean("principal").notNull().default(false),
  enviadoPor: uuid("enviado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export type TutorialPdf = typeof tutorialPdfs.$inferSelect;
export type InsertTutorialPdf = typeof tutorialPdfs.$inferInsert;

export const tutorialTopicos = pgTable("tutorial_topicos", {
  id: uuid("id").primaryKey().defaultRandom(),
  papel: text("papel").notNull(),
  titulo: text("titulo").notNull(),
  criadoPor: uuid("criado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export type TutorialTopico = typeof tutorialTopicos.$inferSelect;
export type InsertTutorialTopico = typeof tutorialTopicos.$inferInsert;

export const tutorialVideos = pgTable("tutorial_videos", {
  id: uuid("id").primaryKey().defaultRandom(),
  topicoId: uuid("topico_id")
    .notNull()
    .references(() => tutorialTopicos.id, { onDelete: "cascade" }),
  versao: integer("versao").notNull(),
  nomeArquivo: text("nome_arquivo").notNull(),
  storagePath: text("storage_path").notNull(),
  tamanhoBytes: integer("tamanho_bytes").notNull(),
  principal: boolean("principal").notNull().default(false),
  enviadoPor: uuid("enviado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export type TutorialVideo = typeof tutorialVideos.$inferSelect;
export type InsertTutorialVideo = typeof tutorialVideos.$inferInsert;

// Status público — sem dados sensíveis, seguro para enviar ao client
export interface RtCertificadoStatus {
  temCertificado: boolean;
  senhaConfigurada: boolean;
  nomeArquivo?: string;
  criadoEm?: string;
}

export const insertEmpresaSchema = createInsertSchema(empresas, {
  cidade: z.string().min(1),
  tagParceiro: z.string().trim().toUpperCase().regex(/^[A-Z][A-Z0-9]{0,9}$/).nullable().optional(),
}).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export const insertUserProfileSchema = createInsertSchema(userProfiles).omit({
  criadoEm: true,
  atualizadoEm: true,
});
export const insertUserInviteSchema = createInsertSchema(userInvites).omit({
  id: true,
  criadoEm: true,
  usos: true,
});
export const insertInviteCodeSchema = createInsertSchema(inviteCodes).omit({
  id: true,
  criadoEm: true,
  usos: true,
});
export const insertLaudoSchema = createInsertSchema(laudos)
  .omit({ criadoEm: true, atualizadoEm: true, aguardandoCreditoDesde: true, moduloDetranVeiculoFoto: true, envioTvSolicitadoEm: true,
    baixa: true, baixaEm: true, baixaPor: true, baixaChaveNota: true, baixaNotaPath: true, baixaNotaNome: true, baixaNotaMime: true })
  .extend({ dataAssinatura: z.coerce.date().optional().nullable() });
export const insertPecaSchema = createInsertSchema(pecas).omit({
  id: true,
  criadoEm: true,
});
export const insertFaturaSchema = createInsertSchema(faturas).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export const insertEmailLogSchema = createInsertSchema(emailLogs).omit({
  id: true,
  criadoEm: true,
});
export const insertLancamentoSchema = createInsertSchema(lancamentos).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export const insertLoteSchema = createInsertSchema(lotes).omit({
  id: true,
  criadoEm: true,
  qtdLaudos: true,
  fechado: true,
  fechadoEm: true,
});
export const insertPerguntaSchema = createInsertSchema(perguntas).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export const insertPecaPerguntaSchema = createInsertSchema(pecaPerguntas);
export const insertVeiculoSchema = createInsertSchema(veiculos).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export const insertLaudoPecaSchema = createInsertSchema(laudoPecas).omit({
  id: true,
  criadoEm: true,
});
export const insertLaudoPecaRespostaSchema = createInsertSchema(
  laudoPecaRespostas,
).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export const insertConfiguracoesVistoriadoraSchema = createInsertSchema(
  configuracoesVistoriadora,
).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export const insertConfigPrecosVistoriadoraSchema = createInsertSchema(
  configPrecosVistoriadora,
).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});
export const insertFichaTecnicaElaboracaoSchema = createInsertSchema(
  fichaTecnicaElaboracao,
).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});

export type Empresa = typeof empresas.$inferSelect & { podeEditarTagParceiro?: boolean };
export type UserProfile = typeof userProfiles.$inferSelect;
export type InviteCode = typeof inviteCodes.$inferSelect;
export type UserInvite = typeof userInvites.$inferSelect;
export type Laudo = typeof laudos.$inferSelect;
export type Peca = typeof pecas.$inferSelect;
export type Lote = typeof lotes.$inferSelect;
export type Pergunta = typeof perguntas.$inferSelect;
export type PerguntaPadrao = typeof perguntasPadraoTipo.$inferSelect;
export type BancoPergunta = typeof bancoPerguntasGlobais.$inferSelect;
export type PecaPergunta = typeof pecaPerguntas.$inferSelect;
export type Fatura = typeof faturas.$inferSelect;
export type EmailLog = typeof emailLogs.$inferSelect;
export type FaturaItem = typeof faturaItens.$inferSelect;
export type PrecosPlataforma = typeof precosPlataforma.$inferSelect;
export type ConfigFinanceiroEmpresa =
  typeof configFinanceiroEmpresa.$inferSelect;
export type Lancamento = typeof lancamentos.$inferSelect;
export type Veiculo = typeof veiculos.$inferSelect;
/** Veículo como a API o devolve: `temLaudo` (qualquer laudo, em qualquer status) deixa o cadastro somente leitura. */
export type VeiculoComLaudo = Veiculo & { temLaudo: boolean };
export type LaudoPeca = typeof laudoPecas.$inferSelect;
export type LaudoPecaResposta = typeof laudoPecaRespostas.$inferSelect;
export type ConfiguracoesVistoriadora =
  typeof configuracoesVistoriadora.$inferSelect;
export type FichaTecnicaElaboracao = typeof fichaTecnicaElaboracao.$inferSelect;
export type ConfigPrecosVistoriadora =
  typeof configPrecosVistoriadora.$inferSelect;

export type InsertEmpresa = z.infer<typeof insertEmpresaSchema>;
export type InsertUserProfile = z.infer<typeof insertUserProfileSchema>;
export type InsertInviteCode = z.infer<typeof insertInviteCodeSchema>;
export type InsertUserInvite = z.infer<typeof insertUserInviteSchema>;
export type InsertLaudo = z.infer<typeof insertLaudoSchema>;
export type InsertPeca = z.infer<typeof insertPecaSchema>;
export type InsertLote = z.infer<typeof insertLoteSchema>;
export type InsertPergunta = z.infer<typeof insertPerguntaSchema>;
export type InsertPecaPergunta = z.infer<typeof insertPecaPerguntaSchema>;
export type InsertFatura = z.infer<typeof insertFaturaSchema>;
export type InsertEmailLog = z.infer<typeof insertEmailLogSchema>;
export type InsertLancamento = z.infer<typeof insertLancamentoSchema>;
export type InsertVeiculo = z.infer<typeof insertVeiculoSchema>;
export type InsertLaudoPeca = z.infer<typeof insertLaudoPecaSchema>;
export type InsertLaudoPecaResposta = z.infer<
  typeof insertLaudoPecaRespostaSchema
>;
export type InsertConfiguracoesVistoriadora = z.infer<
  typeof insertConfiguracoesVistoriadoraSchema
>;
export type InsertFichaTecnicaElaboracao = z.infer<
  typeof insertFichaTecnicaElaboracaoSchema
>;
export type InsertConfigPrecosVistoriadora = z.infer<
  typeof insertConfigPrecosVistoriadoraSchema
>;

export interface UfSequencia {
  uf: string;
  sequencia: number;
}

export interface ValoresPorTipo {
  auto_leve?: string;
  moto?: string;
  caminhao?: string;
  onibus?: string;
}

export type FinanceiroStats = {
  totalReceber: number;
  totalRecebido: number;
  totalVencido: number;
  totalDespesas: number;
};

export type LaudoPecaRespostaWithPergunta = LaudoPecaResposta & {
  pergunta?: Pergunta;
};

export type LaudoPecaWithRespostas = LaudoPeca & {
  respostas?: LaudoPecaRespostaWithPergunta[];
};

export type LaudoWithDetails = Laudo & {
  vistoriadora?: Empresa;
  parceiro?: Empresa;
  tecnicoVistoria?: UserProfile;
  responsavelTecnico?: UserProfile;
  veiculo?: Veiculo & { fotos?: VeiculoFoto[] };
  laudoPecas?: LaudoPecaWithRespostas[];
  /** ART a exibir no laudo, resolvida conforme configuracoesVistoriadora (artMultipla/artPorRt). */
  artResolvida?: { numero: string; vencimento: string | null } | null;
  /** Indica se o RT do laudo tem certificado digital ICP-Brasil cadastrado (rt_certificados). */
  rtTemCertificado?: boolean;
};

export const empresaTipos = [
  "OWNER",
  "VISTORIADORA",
  "PARCEIRO_DE_NEGOCIOS",
] as const;
export const laudoStatuses = [
  "rascunho",
  "enviado_analise",
  "em_vistoria",
  "em_avaliacao_rt",
  "assinado",
  "finalizado",
] as const;
export const tipoVeiculos = [
  "auto_leve",
  "moto",
  "caminhao",
  "onibus",
] as const;
export const tipoDestinacoes = [
  "venda_imediata",
  "necessidade_reparo",
  "inexistente",
] as const;
export const userRoles = [
  "owner",
  "admin",
  "rt",
  "tv",
  "parceiro_negocios",
] as const;

/**
 * Converte um LaudoWithDetails para um Partial<InsertLaudo>
 * Transforma objetos aninhados em seus IDs correspondentes
 */
export function laudoWithDetailsToInsertLaudo(
  laudo: LaudoWithDetails,
): Partial<InsertLaudo> {
  return {
    id: laudo.id,
    laudoId: laudo.laudoId,
    vistoriadoraId: laudo.vistoriadoraId,
    parceiroId: laudo.parceiroId,
    cartela: laudo.cartela,
    status: laudo.status,
    tecnicoVistoriaId: laudo.tecnicoVistoria?.id ?? laudo.tecnicoVistoriaId,
    responsavelTecnicoId:
      laudo.responsavelTecnico?.id ?? laudo.responsavelTecnicoId,
    assinaturaUrl: laudo.assinaturaUrl,
    pdfUrl: laudo.pdfUrl,
    dataVistoria: laudo.dataVistoria,
    dataAssinatura: laudo.dataAssinatura,
    veiculoId: laudo.veiculo?.id ?? laudo.veiculoId,
    criadoPor: laudo.criadoPor,
  };
}
